import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  LayoutDashboard,
  LoaderCircle,
  LogOut,
  Menu,
  ShoppingBag,
  UserRound,
  X,
} from "lucide-react";
import { useEffect, useState, type MouseEvent } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/features/auth/AuthContext";
import { useCart } from "@/features/sales/storefront/cart/CartContext";
import {
  useCurrency,
  type DisplayCurrency,
} from "@/features/sales/storefront/currency/CurrencyContext";

const nav = [
  { label: "Tienda", to: "/productos/catalogo" as const },
  { label: "Pódcast", to: "/podcast" as const },
  { label: "Mis compras", to: "/productos/mis-compras" as const },
];
const PROMO_BANNER_DISMISSED_KEY = "socrattica:newsletter-promo:dismissed";

export function SiteHeader() {
  const navigate = useNavigate();
  const { count } = useCart();
  const { user, isAdmin, logout } = useAuth();
  const { currency, setCurrency, isLiveRate } = useCurrency();
  const [loggingOut, setLoggingOut] = useState(false);
  const [showPromoBanner, setShowPromoBanner] = useState(true);
  const pathname = useLocation({ select: (location) => location.pathname });
  const accountPath = user ? ("/cuenta" as const) : ("/acceso" as const);
  const navigation =
    user && !isAdmin
      ? nav.map((item) =>
          item.label === "Mis compras" ? { ...item, to: "/cuenta" as const } : item,
        )
      : nav;
  const showCurrencySelector =
    pathname === "/productos/catalogo" || pathname === "/productos/carrito";

  useEffect(() => {
    try {
      setShowPromoBanner(window.localStorage.getItem(PROMO_BANNER_DISMISSED_KEY) !== "true");
    } catch {
      // El banner sigue funcionando aunque el navegador no permita localStorage.
    }
  }, []);

  function dismissPromoBanner() {
    setShowPromoBanner(false);
    try {
      window.localStorage.setItem(PROMO_BANNER_DISMISSED_KEY, "true");
    } catch {
      // El cierre sigue funcionando durante la sesión actual.
    }
  }

  function handlePromoClick(event: MouseEvent<HTMLAnchorElement>) {
    if (pathname !== "/podcast") return;
    const newsletter = document.getElementById("newsletter");
    if (!newsletter) return;
    event.preventDefault();
    window.history.replaceState(null, "", "/podcast#newsletter");
    newsletter.scrollIntoView({ behavior: "smooth", block: "start" });
    // La cuadrícula del pódcast termina de cargar después del primer scroll y
    // puede desplazar el formulario. Reajustamos el destino mientras se estabiliza.
    [300, 900, 1_800].forEach((delay) => {
      window.setTimeout(() => {
        document.getElementById("newsletter")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, delay);
    });
  }

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await logout();
      await navigate({ to: "/", replace: true });
    } catch {
      toast.error("No pudimos cerrar la sesión.");
      setLoggingOut(false);
    }
  }

  return (
    <>
    <header className="fixed inset-x-0 top-0 z-40 border-b border-black/10 bg-white/95 backdrop-blur-md">
      {showPromoBanner && (
        <div className="relative border-b border-white/15 bg-black text-white">
          <a
            href="/podcast#newsletter"
            onClick={handlePromoClick}
            className="group flex min-h-10 items-center justify-center gap-2 px-12 py-2 text-center text-[11px] font-semibold leading-4 tracking-wide transition hover:bg-white hover:text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-inset sm:text-xs"
          >
            <span>Suscríbete al pódcast y recibe 15% de descuento en nuestros productos</span>
            <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
          </a>
          <button
            type="button"
            onClick={dismissPromoBanner}
            aria-label="Cerrar promoción"
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-white/70 transition hover:bg-white/15 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-4"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <div className="mx-auto flex h-16 max-w-7xl min-w-0 items-center justify-between gap-3 px-4 sm:gap-5 sm:px-6">
        <Link
          to="/"
          aria-label="Socrattica, inicio"
          className="inline-flex items-center gap-2 rounded-md text-sm font-bold tracking-[-0.03em] text-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
        >
          <span
            aria-hidden="true"
            className="h-10 w-10 rounded-full bg-[url('/logo.png')] bg-cover bg-center"
          />
          <span className="text-base">Socrattica</span>
        </Link>

        <nav
          className="hidden items-center gap-7 text-xs font-medium md:flex"
          aria-label="Principal"
        >
          {navigation.map((item) => (
            <Link
              key={item.label}
              to={item.to}
              activeProps={{ className: "text-black" }}
              inactiveProps={{ className: "text-black/70" }}
              className="transition-colors hover:text-black"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          {showCurrencySelector && (
            <select
              value={currency}
              onChange={(event) => setCurrency(event.target.value as DisplayCurrency)}
              aria-label="Moneda para visualizar precios"
              title={
                currency === "USD"
                  ? isLiveRate
                    ? "Conversión estimada con tasa actualizada"
                    : "Conversión estimada"
                  : "Precios en pesos colombianos"
              }
              className="h-9 cursor-pointer rounded-lg border border-black/20 bg-white px-2 text-xs font-semibold text-black outline-none transition focus:border-black focus:ring-2 focus:ring-black/10"
            >
              <option value="COP">COP</option>
              <option value="USD">USD</option>
            </select>
          )}
          {isAdmin && (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to="/admin/dashboard">
                <LayoutDashboard />
                Panel
              </Link>
            </Button>
          )}
          {!isAdmin && (
            <Button asChild variant="ghost" size="sm" className="hidden sm:inline-flex">
              <Link to={accountPath}>
                <UserRound />
                {user ? "Mi cuenta" : "Ingresar"}
              </Link>
            </Button>
          )}
          <Button asChild size="sm" className="relative">
            <Link to="/productos/carrito" aria-label={`Carrito con ${count} artículos`}>
              <ShoppingBag />
              <span className="hidden sm:inline">Carrito</span>
              {count > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-xs text-black">
                  {count}
                </span>
              )}
            </Link>
          </Button>
          {user && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              disabled={loggingOut}
              onClick={() => void handleLogout()}
              aria-label="Cerrar sesión"
              title="Cerrar sesión"
            >
              {loggingOut ? <LoaderCircle className="animate-spin" /> : <LogOut />}
            </Button>
          )}

          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="md:hidden" aria-label="Abrir menú">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-l border-black bg-white p-6">
              <div className="mb-10 flex items-center gap-2 text-base font-bold">
                <span
                  aria-hidden="true"
                  className="h-10 w-10 rounded-full bg-[url('/logo.png')] bg-cover bg-center"
                />
                Socrattica
              </div>
              <nav className="flex flex-col border-t border-black/10" aria-label="Menú móvil">
                {[
                  ...navigation,
                  ...(!isAdmin
                    ? [{ label: user ? "Mi cuenta" : "Ingresar", to: accountPath }]
                    : []),
                ].map((item) => (
                  <Link
                    key={item.label}
                    to={item.to}
                    className="border-b border-black/10 py-4 text-sm font-medium"
                  >
                    {item.label}
                  </Link>
                ))}
                {isAdmin && (
                  <Link to="/admin/dashboard" className="border-b border-black/10 py-4 text-sm">
                    Panel de administración
                  </Link>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
    <div
      aria-hidden="true"
      className={showPromoBanner ? "h-[104px] shrink-0" : "h-16 shrink-0"}
    />
    </>
  );
}
